// Budget API service layer
// Centralizes all Budget-related API calls with consistent error handling

import config from '../../../config';
import Notify from '../../../shared/components/widgets/Notify';

/**
 * Creates standard request headers with authentication
 * @param {string} jwtToken - JWT authentication token
 * @returns {Object} - Headers object
 */
function getAuthHeaders(jwtToken) {
  return {
    ...config.jsonHeader,
    authorization: `JWT ${jwtToken}`,
  };
}

/**
 * Standard error handler for API calls
 * @param {Error} error - The error object
 * @param {string} context - Context message for the error
 * @param {boolean} showNotification - Whether to show a notification (default: true)
 */
export function handleApiError(error, context, showNotification = true) {
  console.error(`[Budget API Error] ${context}:`, error);
  if (showNotification) {
    Notify.show('danger', 'Error', `${context}: ${error.message || error}`);
  }
  throw error;
}

/**
 * Fetches budget group data
 * @param {Object} params - Parameters
 * @param {number} params.accountId - Account ID
 * @param {string} params.retailer - Retailer name
 * @param {string} params.platform - Platform key
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Budget group data
 */
export async function getBudgetGroups({ accountId, retailer, platform, jwtToken }) {
  const retailerPath = `${retailer.toLowerCase()}/`;
  const url = `${config.serverUrl}/accounts/${retailerPath}budget-group-daily?accountId=${accountId}&platform=${platform}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Failed to fetch budget groups');
  }
}

/**
 * Updates or creates a budget group
 * @param {Object} params - Parameters
 * @param {Object} params.data - Budget group data
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Response data
 */
export async function saveBudgetGroup({ data, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-group-daily`;

  const requestOptions = {
    method: 'POST',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify(data),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    Notify.show('primary', 'Success', 'Budget group has been updated successfully.');
    return result;
  } catch (error) {
    return handleApiError(error, 'Failed to save budget group');
  }
}

/**
 * Fetches budget events
 * @param {Object} params - Parameters
 * @param {number} params.accountId - Account ID
 * @param {string} params.retailer - Retailer key
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Budget events data
 */
export async function getBudgetEvents({ accountId, retailer, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-group-event-daily?accountId=${accountId}&retailer=${retailer}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Failed to fetch budget events');
  }
}

/**
 * Updates or creates a budget event
 * @param {Object} params - Parameters
 * @param {Object} params.data - Budget event data
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Response data
 */
export async function saveBudgetEvent({ data, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-group-event-daily`;

  const requestOptions = {
    method: 'POST',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify(data),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    Notify.show('primary', 'Success', 'Budget event has been updated successfully.');
    return result;
  } catch (error) {
    return handleApiError(error, 'Failed to save budget event');
  }
}

/**
 * Fetches budget account data
 * @param {Object} params - Parameters
 * @param {number} params.accountId - Account ID
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Budget account data
 */
export async function getBudgetAccount({ accountId, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-account-daily?accountId=${accountId}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    handleApiError(error, 'Failed to fetch budget account data', false);
    return {};
  }
}

/**
 * Fetches retailer-specific budget account data
 * @param {Object} params - Parameters
 * @param {number} params.accountId - Account ID
 * @param {string} params.retailer - Retailer name (amazon, walmart, etc.)
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Budget account data
 */
export async function getBudgetAccountByRetailer({ accountId, retailer, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-account-daily?accountId=${accountId}&retailer=${retailer}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    handleApiError(error, 'Failed to fetch retailer-specific budget account data', false);
    return {};
  }
}

/**
 * Saves budget account settings
 * @param {Object} params - Parameters
 * @param {Object} params.data - Budget account data to save
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Response data
 */
export async function saveBudgetAccount({ data, jwtToken }) {
  const url = `${config.serverUrl}/accounts/budget-account-daily`;

  const requestOptions = {
    method: 'POST',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify(data),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    Notify.show('primary', 'Success', 'Budget account settings have been updated successfully.');
    return result;
  } catch (error) {
    return handleApiError(error, 'Failed to save budget account settings');
  }
}

/**
 * Updates catalog items
 * @param {Object} params - Parameters
 * @param {Object} params.data - Catalog update data
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Response data
 */
export async function updateCatalogItems({ data, jwtToken }) {
  const url = `${config.serverUrl}/catalogs`;

  const requestOptions = {
    method: 'PUT',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify(data),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    Notify.show('primary', 'Success', 'Catalog items have been updated successfully.');
    return result;
  } catch (error) {
    return handleApiError(error, 'Failed to update catalog items');
  }
}

/**
 * Maps a raw Google Ads language constant row (from BigQuery) to a
 * standard {label, value} option shape used throughout the Budget forms.
 * @param {Object} row - Raw row (resource_name, name, language_code, criterion_id, targetable)
 * @returns {Object} - {label, value}
 */
function mapLanguageConstantRow(row) {
  return {
    label: row.name,
    value: row.resource_name,
  };
}

/**
 * Fuzzy-searches Google Ads language constants by name, language code, or criterion_id.
 * Platform-driven data only - no accountId required.
 * @param {Object} params - Parameters
 * @param {string} params.searchTerm - Search term (optional)
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Array>} - Array of {label, value} options
 */
export async function searchGoogleLanguageConstants({ searchTerm, jwtToken }) {
  let url = `${config.serverUrl}/google/language-constants/search`;
  if (searchTerm) {
    url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
  }

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const rows = await response.json();
    return Array.isArray(rows) ? rows.map(mapLanguageConstantRow) : [];
  } catch (error) {
    handleApiError(error, 'Failed to search Google language constants', false);
    return [];
  }
}

/**
 * Looks up Google Ads language constants by one or more resource_name values.
 * Used to resolve stored keys (e.g. "languageConstants/1000") to human-readable
 * labels (e.g. "English") when a budget form is loaded with existing data.
 * @param {Object} params - Parameters
 * @param {Array<string>} params.resourceNames - Resource names to look up
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Array>} - Array of {label, value} options
 */
export async function getGoogleLanguageConstantsByResourceNames({ resourceNames, jwtToken }) {
  if (!resourceNames || resourceNames.length === 0) {
    return [];
  }

  const url = `${config.serverUrl}/google/language-constants/by-resource-name?resourceNames=${encodeURIComponent(resourceNames.join(','))}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const rows = await response.json();
    return Array.isArray(rows) ? rows.map(mapLanguageConstantRow) : [];
  } catch (error) {
    handleApiError(error, 'Failed to fetch Google language constants', false);
    return [];
  }
}

/**
 * Maps a raw Google Ads geo target constant row (from BigQuery) to a
 * standard {label, value} option shape used throughout the Budget forms.
 * @param {Object} row - Raw row (resourceName, name, canonicalName, countryCode, targetType, uiCategory, status)
 * @returns {Object} - {label, value}
 */
function mapGeoConstantRow(row) {
  return {
    label: row.name,
    value: row.resourceName,
  };
}

/**
 * Fuzzy-searches Google Ads geo target constants by name, canonicalName, countryCode,
 * or criterionId. Optionally filter by uiCategory (COUNTRY, STATE, CITY, POSTAL_CODE)
 * and/or countryCode. Platform-driven data only - no accountId required.
 * @param {Object} params - Parameters
 * @param {string} params.searchTerm - Search term (optional)
 * @param {string} params.uiCategory - Comma-separated categories to filter by (optional)
 * @param {string} params.countryCode - Comma-separated country codes to filter by (optional)
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Array>} - Array of {label, value} options
 */
export async function searchGoogleGeoConstants({
  searchTerm, uiCategory, countryCode, jwtToken,
}) {
  const url = new URL(`${config.serverUrl}/google/geo-constants/search`);
  if (searchTerm) url.searchParams.set('searchTerm', searchTerm);
  if (uiCategory) url.searchParams.set('uiCategory', uiCategory);
  if (countryCode) url.searchParams.set('countryCode', countryCode);

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url.toString(), requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const rows = await response.json();
    return Array.isArray(rows) ? rows.map(mapGeoConstantRow) : [];
  } catch (error) {
    handleApiError(error, 'Failed to search Google geo target constants', false);
    return [];
  }
}

/**
 * Looks up Google Ads geo target constants by one or more resourceName values.
 * Used to resolve stored keys (e.g. "geoTargetConstants/2840") to human-readable
 * labels (e.g. "United States") when a budget form is loaded with existing data.
 * @param {Object} params - Parameters
 * @param {Array<string>} params.resourceNames - Resource names to look up
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Array>} - Array of {label, value} options
 */
export async function getGoogleGeoConstantsByResourceNames({ resourceNames, jwtToken }) {
  if (!resourceNames || resourceNames.length === 0) {
    return [];
  }

  const url = `${config.serverUrl}/google/geo-constants/by-resource-name?resourceNames=${encodeURIComponent(resourceNames.join(','))}`;

  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const rows = await response.json();
    return Array.isArray(rows) ? rows.map(mapGeoConstantRow) : [];
  } catch (error) {
    handleApiError(error, 'Failed to fetch Google geo target constants', false);
    return [];
  }
}

/**
 * Maps a raw Meta Graph API adgeolocation search result row to a standard
 * {label, value} option shape used throughout the Budget forms.
 * @param {Object} row - Raw row (key, name, type, supports_city, supports_region)
 * @returns {Object} - {label, value}
 */
function mapMetaGeoLocationRow(row) {
  return {
    label: row.name,
    value: row.key,
  };
}

/**
 * Fuzzy-searches Meta Graph API geo-location targeting options (Countries today).
 * Uses the generic Meta search proxy (POST /meta/search/get -> Graph API /search,
 * type=adgeolocation). Meta ties this search to a specific ad account, so
 * accountId is required (unlike the platform-driven Google constants endpoints).
 * Meta's search `q` parameter is required, so this is only used interactively
 * as the user types (see getMetaCountriesByResourceNames for initial-value
 * label resolution, which does not depend on a live search call).
 * @param {Object} params - Parameters
 * @param {number|string} params.accountId - Internal account ID
 * @param {string} params.searchTerm - Search term
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Array>} - Array of {label, value} options
 */
export async function searchMetaCountries({ accountId, searchTerm, jwtToken }) {
  if (!searchTerm) {
    return [];
  }

  const url = `${config.serverUrl}/meta/search/get`;

  const requestOptions = {
    method: 'POST',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify({
      accountInfo: { accountId },
      data: {
        type: 'adgeolocation',
        location_types: ['country'],
        q: searchTerm,
        limit: 50,
      },
    }),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    const rows = Array.isArray(result) ? result : result?.data;
    return Array.isArray(rows) ? rows.map(mapMetaGeoLocationRow) : [];
  } catch (error) {
    handleApiError(error, 'Failed to search Meta countries', false);
    return [];
  }
}

// Standard ISO 3166-1 alpha-2 country codes and English short names. Meta's
// adgeolocation "key" for countries is the ISO alpha-2 code (e.g. "US",
// "GB"), a fixed/stable standard that doesn't change. Since Meta's search
// API has no dedicated "lookup by key" endpoint and requires a non-empty
// `q`, this static map is used only to resolve stored keys back to display
// labels when a budget form loads with existing data - all interactive
// searching/selecting still goes through the live Meta API.
const ISO_COUNTRY_NAMES = {
  AF: 'Afghanistan',
  AX: 'Åland Islands',
  AL: 'Albania',
  DZ: 'Algeria',
  AS: 'American Samoa',
  AD: 'Andorra',
  AO: 'Angola',
  AI: 'Anguilla',
  AQ: 'Antarctica',
  AG: 'Antigua and Barbuda',
  AR: 'Argentina',
  AM: 'Armenia',
  AW: 'Aruba',
  AU: 'Australia',
  AT: 'Austria',
  AZ: 'Azerbaijan',
  BS: 'Bahamas',
  BH: 'Bahrain',
  BD: 'Bangladesh',
  BB: 'Barbados',
  BY: 'Belarus',
  BE: 'Belgium',
  BZ: 'Belize',
  BJ: 'Benin',
  BM: 'Bermuda',
  BT: 'Bhutan',
  BO: 'Bolivia',
  BQ: 'Bonaire, Sint Eustatius and Saba',
  BA: 'Bosnia and Herzegovina',
  BW: 'Botswana',
  BV: 'Bouvet Island',
  BR: 'Brazil',
  IO: 'British Indian Ocean Territory',
  BN: 'Brunei Darussalam',
  BG: 'Bulgaria',
  BF: 'Burkina Faso',
  BI: 'Burundi',
  CV: 'Cabo Verde',
  KH: 'Cambodia',
  CM: 'Cameroon',
  CA: 'Canada',
  KY: 'Cayman Islands',
  CF: 'Central African Republic',
  TD: 'Chad',
  CL: 'Chile',
  CN: 'China',
  CX: 'Christmas Island',
  CC: 'Cocos (Keeling) Islands',
  CO: 'Colombia',
  KM: 'Comoros',
  CG: 'Congo',
  CD: 'Congo (Democratic Republic)',
  CK: 'Cook Islands',
  CR: 'Costa Rica',
  CI: "Côte d'Ivoire",
  HR: 'Croatia',
  CU: 'Cuba',
  CW: 'Curaçao',
  CY: 'Cyprus',
  CZ: 'Czechia',
  DK: 'Denmark',
  DJ: 'Djibouti',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  EC: 'Ecuador',
  EG: 'Egypt',
  SV: 'El Salvador',
  GQ: 'Equatorial Guinea',
  ER: 'Eritrea',
  EE: 'Estonia',
  SZ: 'Eswatini',
  ET: 'Ethiopia',
  FK: 'Falkland Islands',
  FO: 'Faroe Islands',
  FJ: 'Fiji',
  FI: 'Finland',
  FR: 'France',
  GF: 'French Guiana',
  PF: 'French Polynesia',
  TF: 'French Southern Territories',
  GA: 'Gabon',
  GM: 'Gambia',
  GE: 'Georgia',
  DE: 'Germany',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GR: 'Greece',
  GL: 'Greenland',
  GD: 'Grenada',
  GP: 'Guadeloupe',
  GU: 'Guam',
  GT: 'Guatemala',
  GG: 'Guernsey',
  GN: 'Guinea',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HT: 'Haiti',
  HM: 'Heard Island and McDonald Islands',
  VA: 'Holy See',
  HN: 'Honduras',
  HK: 'Hong Kong',
  HU: 'Hungary',
  IS: 'Iceland',
  IN: 'India',
  ID: 'Indonesia',
  IR: 'Iran',
  IQ: 'Iraq',
  IE: 'Ireland',
  IM: 'Isle of Man',
  IL: 'Israel',
  IT: 'Italy',
  JM: 'Jamaica',
  JP: 'Japan',
  JE: 'Jersey',
  JO: 'Jordan',
  KZ: 'Kazakhstan',
  KE: 'Kenya',
  KI: 'Kiribati',
  KP: 'North Korea',
  KR: 'South Korea',
  KW: 'Kuwait',
  KG: 'Kyrgyzstan',
  LA: 'Laos',
  LV: 'Latvia',
  LB: 'Lebanon',
  LS: 'Lesotho',
  LR: 'Liberia',
  LY: 'Libya',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MO: 'Macao',
  MG: 'Madagascar',
  MW: 'Malawi',
  MY: 'Malaysia',
  MV: 'Maldives',
  ML: 'Mali',
  MT: 'Malta',
  MH: 'Marshall Islands',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MU: 'Mauritius',
  YT: 'Mayotte',
  MX: 'Mexico',
  FM: 'Micronesia',
  MD: 'Moldova',
  MC: 'Monaco',
  MN: 'Mongolia',
  ME: 'Montenegro',
  MS: 'Montserrat',
  MA: 'Morocco',
  MZ: 'Mozambique',
  MM: 'Myanmar',
  NA: 'Namibia',
  NR: 'Nauru',
  NP: 'Nepal',
  NL: 'Netherlands',
  NC: 'New Caledonia',
  NZ: 'New Zealand',
  NI: 'Nicaragua',
  NE: 'Niger',
  NG: 'Nigeria',
  NU: 'Niue',
  NF: 'Norfolk Island',
  MK: 'North Macedonia',
  MP: 'Northern Mariana Islands',
  NO: 'Norway',
  OM: 'Oman',
  PK: 'Pakistan',
  PW: 'Palau',
  PS: 'Palestine',
  PA: 'Panama',
  PG: 'Papua New Guinea',
  PY: 'Paraguay',
  PE: 'Peru',
  PH: 'Philippines',
  PN: 'Pitcairn',
  PL: 'Poland',
  PT: 'Portugal',
  PR: 'Puerto Rico',
  QA: 'Qatar',
  RE: 'Réunion',
  RO: 'Romania',
  RU: 'Russia',
  RW: 'Rwanda',
  BL: 'Saint Barthélemy',
  SH: 'Saint Helena',
  KN: 'Saint Kitts and Nevis',
  LC: 'Saint Lucia',
  MF: 'Saint Martin',
  PM: 'Saint Pierre and Miquelon',
  VC: 'Saint Vincent and the Grenadines',
  WS: 'Samoa',
  SM: 'San Marino',
  ST: 'Sao Tome and Principe',
  SA: 'Saudi Arabia',
  SN: 'Senegal',
  RS: 'Serbia',
  SC: 'Seychelles',
  SL: 'Sierra Leone',
  SG: 'Singapore',
  SX: 'Sint Maarten',
  SK: 'Slovakia',
  SI: 'Slovenia',
  SB: 'Solomon Islands',
  SO: 'Somalia',
  ZA: 'South Africa',
  GS: 'South Georgia',
  SS: 'South Sudan',
  ES: 'Spain',
  LK: 'Sri Lanka',
  SD: 'Sudan',
  SR: 'Suriname',
  SJ: 'Svalbard and Jan Mayen',
  SE: 'Sweden',
  CH: 'Switzerland',
  SY: 'Syria',
  TW: 'Taiwan',
  TJ: 'Tajikistan',
  TZ: 'Tanzania',
  TH: 'Thailand',
  TL: 'Timor-Leste',
  TG: 'Togo',
  TK: 'Tokelau',
  TO: 'Tonga',
  TT: 'Trinidad and Tobago',
  TN: 'Tunisia',
  TR: 'Turkey',
  TM: 'Turkmenistan',
  TC: 'Turks and Caicos Islands',
  TV: 'Tuvalu',
  UG: 'Uganda',
  UA: 'Ukraine',
  AE: 'United Arab Emirates',
  GB: 'United Kingdom',
  US: 'United States',
  UM: 'United States Minor Outlying Islands',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VU: 'Vanuatu',
  VE: 'Venezuela',
  VN: 'Vietnam',
  VG: 'Virgin Islands (British)',
  VI: 'Virgin Islands (U.S.)',
  WF: 'Wallis and Futuna',
  EH: 'Western Sahara',
  YE: 'Yemen',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
};

/**
 * Resolves Meta country "key" values (e.g. "US") back to human-readable labels
 * (e.g. "United States") when a budget form is loaded with existing data.
 * Uses the static ISO country code map (see ISO_COUNTRY_NAMES) rather than a
 * live API call, since Meta's search endpoint requires a non-empty query and
 * has no dedicated "lookup by key" endpoint.
 * @param {Object} params - Parameters
 * @param {Array<string>} params.resourceNames - Country keys to resolve
 * @returns {Promise<Array>} - Array of {label, value} options
 */
export async function getMetaCountriesByResourceNames({ resourceNames }) {
  if (!resourceNames || resourceNames.length === 0) {
    return [];
  }

  return resourceNames.map(key => ({
    label: ISO_COUNTRY_NAMES[key] || key,
    value: key,
  }));
}

/**
 * Fetches platform data (spend/sales)
 * @param {Object} params - Parameters
 * @param {string} params.url - API endpoint URL
 * @param {string} params.jwtToken - JWT token
 * @returns {Promise<Object>} - Platform data
 */
export async function getPlatformData({ url, jwtToken }) {
  const requestOptions = {
    method: 'GET',
    headers: getAuthHeaders(jwtToken),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    console.warn('Platform data not available:', error.message);
    // Return empty structure instead of throwing
    return {
      by_date: [],
      totals: { total_sales: 0, total_spend: 0 },
      by_id: { group: {} },
    };
  }
}
